import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { axiosInstance } from '@/lib/axios'
import { getCompanies } from 'app/actions/companySelection'
import { getCompanyDetails } from 'app/actions/company'
import { getUserInfo as getStoredUserInfo } from 'app/services/local/roleService'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'

import { homePath } from '@/modules/home'
import { isUnusableServiceCompany } from 'app/constants/companyType'
import { clearPrintoutSelection } from '@/modules/printouts/stores/printoutSelectionStore'
import { useCompanySelectionStore } from '@/modules/shared/stores/companySelectionStore'
import { KEYS } from '@/modules/shared/hooks/usePersistentState'
import { Loader } from '@/ui'

/**
 * Konto usługowe nie ma wierszy `CompanyRole` - dostęp do firm ze swojego zakresu
 * wylicza serwer, więc pusta lista ról nie znaczy u niego „brak dostępu".
 */
const hasNoRoles = (info: any) =>
  info &&
  !info.globalAdmin &&
  !info.hasServiceAccountAccess &&
  (!info.roles || info.roles.length === 0)

/**
 * `/companySelection` - przekierowanie do firmy, nie ekran wyboru.
 *
 * Zastępuje `app/components/companySelection/companySelection.tsx` (klasa na
 * `ValidatorBase`). Tamten ekran renderował `CompanySelectionPage` zawsze z
 * `autoSelect`, więc listy firm do kliknięcia nigdy nie było widać - tylko spinner
 * i skok do firmy. Wybór firmy robi się dziś przełącznikiem w górnym pasku,
 * a walidacja i `goNext()` ze starej klasy nie miały już czego obsługiwać.
 */
export const CompanySelectionRoute: React.FC = () => {
  const navigate = useNavigate()
  const setCompanies = useCompanySelectionStore((store) => store.setCompanies)
  const chooseCompany = useCompanySelectionStore((store) => store.chooseCompany)

  // Skok ma się wydarzyć raz - bez tego efekt goni własne `setState` w storze.
  const handled = React.useRef(false)

  React.useEffect(() => {
    if (handled.current) return
    handled.current = true

    const run = async () => {
      // Świeżo zarejestrowany użytkownik bez żadnej firmy ma iść wybrać pakiet,
      // a nie oglądać pustą listę.
      const stored = getStoredUserInfo()
      if (hasNoRoles(stored?.userInfo)) {
        navigate('/post-register', { replace: true })
        return
      }
      if (!stored?.userInfo) {
        try {
          const { data } = await axiosInstance.get('/config-api/user')
          if (hasNoRoles(data)) {
            navigate('/post-register', { replace: true })
            return
          }
        } catch {
          // Bez rozstrzygnięcia ról lepiej puścić dalej niż zawiesić na spinnerze.
        }
      }

      const companies: CompanyDetailsModel[] = (await getCompanies()) ?? []
      setCompanies(companies)
      if (companies.length === 0) return

      let rememberedId: string | null = null
      try {
        const raw = localStorage.getItem(KEYS.SELECTED_COMPANY_ID)
        rememberedId = raw ? JSON.parse(raw) : null
      } catch {
        rememberedId = null
      }

      // Firma usługowa nie ma bazy tachografowej, więc `homePath` pod nią zwraca
      // 500. Właściciel konta widzi ją w liście pierwszą, więc bez tego filtru
      // trafiałby po zalogowaniu prosto w zepsuty dashboard.
      const selectable = companies.filter((item) => !isUnusableServiceCompany(item))
      const target =
        (rememberedId &&
          selectable.find((item) => item.id.toString() === rememberedId)?.id) ??
        selectable[0]?.id ??
        companies[0].id
      const targetPath = isUnusableServiceCompany(
        companies.find((item) => item.id.toString() === target.toString()),
      )
        ? `/et/company/${target}/serviceAccount`
        : homePath.replace(':companyId', target)

      try {
        localStorage.setItem(KEYS.SELECTED_COMPANY_ID, JSON.stringify(target))
      } catch {
        // Brak localStorage nie może zablokować wejścia do firmy.
      }

      chooseCompany({ id: target } as CompanyDetailsModel)
      getCompanyDetails(target, true)
      clearPrintoutSelection()

      navigate(targetPath, { replace: true })
    }

    run()
  }, [navigate, setCompanies, chooseCompany])

  return <Loader className="tw-h-screen tw-w-screen" />
}
